import useMediaSelectionActions from '@/actions/useMediaSelectionActions'
import { ExternalEvent } from '@/AppContext'
import FakeMediaStreamTrack from '@/core/AudioEngine/FakeMediaStreamTrack'
import MicrophoneIcon from '@/CustomIcons/MicrophoneIcon'
import MicrophoneOffIcon from '@/CustomIcons/MicrophoneOffIcon'
import TalkbackIcon from '@/CustomIcons/TalkbackIcon'
import VolumeOffIcon from '@/CustomIcons/VolumeOffIcon'
import { ExternalEventEmitter } from '@/ExternalEventEmitter'
import useDebounce from '@/hooks/useDebounce'
import { encodeExtendedDeviceId, makeTalkbackKeyDescriptionString } from '@/Logic'
import { RoomContext, RoomContextType } from '@/RoomContext'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import { keyboardshortcut_talkback } from '@/services/Constants'
import ElectronIPC from '@/services/ElectronIPC'
import { persistent } from '@/services/LocalStorage'
import useAnalytics from '@/services/useAnalytics'
import useRoomActions from '@/services/useRoomActions'
import useUserContextRef from '@/services/useUserContextRef'
import { Button } from '@/UIComponents2/Button'
import MenuButton, { MenuItem } from '@/UIComponents2/MenuButton/MenuButton'
import { Slider } from '@/UIComponents2/Slider'
import { SidebarTooltip as Tooltip } from '@/pages/SessionPage/internal/sidebar/SidebarTooltip'
import { AudioTrackWithInfo, AudioSourceInfo, UserContext, UserContextType } from '@/UserContext'
import { useContext, useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ErrorIndicator } from '@/pages/SessionPage/internal/shared/ErrorIndicator'
import { useViewMode } from '@/pages/SessionPage/internal/shared/useViewMode'
import { DeviceLabelRow } from './DeviceLabelRow'
import { DeviceContainer } from './DeviceContainer'
import { ChannelGroupContainer } from './ChannelGroupContainer'
import { ChannelGroupMeter } from './ChannelGroupMeter'
import { Loader } from '@/components/Loader'
import { Checkbox } from '@/UIComponents2/Checkbox'
import { sessionSettingsTrigger } from '@/pages/SessionPage/SessionPage'
import { env } from '@/services/EnvironmentVariables'
import ClearIcon from '@/CustomIcons/ClearIcon'
import { cn } from '@/utlis/cn'

export function AudioDeviceMe({
    atrack,
    label,
    openAudioInputDeviceSettings,
}: {
    atrack: AudioTrackWithInfo
    label: string
    openAudioInputDeviceSettings: (mediaSource: AudioSourceInfo) => void
}) {
    const { t } = useTranslation()

    const { state: userState } = useContext(UserContext) as UserContextType

    const { userStateRef } = useUserContextRef()

    const [volumeControlError, set_volumeControlError] = useState<string | null>(null)

    const { toggleMyAudioDeviceMute, toggleMyAudioDeviceTalkback, toggleAutoMute, openRecordTalkbackKeyDialog } =
        useRoomActions()

    useEffect(() => {
        if (atrack.info.audioEngineStreamUuid) {
            return
        }

        if (env.OS === 'darwin') {
            if (atrack.info.nativeDeviceId) {
                const isInput = atrack.info.isSystemAudio ? false : true
                const device = {
                    nativeDeviceId: atrack.info.nativeDeviceId,
                    isInput: isInput,
                }
                console.log('getSystemDeviceVolume: %O', device)
                ElectronIPC.getSystemDeviceVolume({ devices: [device] })
            } else {
                set_volumeControlError('device not supported')
            }
        } else {
            set_volumeControlError('os_not_supported')
        }

        if (atrack.info.isMuseAudioShare || atrack.info.isMuseDAWBridge) {
            set_volumeControlError('muse_system_audio')
        }
    }, [])

    useEffect(() => {
        if (!userState.mediaSourceStatus.audioInputs[atrack.info.deviceId]?.isMuted) {
            if (volumeControlError === 'device_muted') {
                set_volumeControlError(null)
            }
        }
    }, [userState.mediaSourceStatus.audioInputs, volumeControlError])

    const mediaSource =
        userStateRef.current.mediaSources.audioInputs.find((a) => a.deviceId === atrack.info.deviceId) || null

    const hasComputerAudioError = (atrack.info.isMuseAudioShare && userState.mediaPreflightStatus.audioComputer.isReady === false)
    const hasDawAudioError = (atrack.info.isMuseDAWBridge && userState.mediaPreflightStatus.audioDaw.isReady === false)
    const hasError = hasComputerAudioError || hasDawAudioError

    return (
        <DeviceContainer>
            <DeviceLabelRow
                label={label}
                info={atrack.info}
                hasError={hasError}
                onOpenSettings={() => mediaSource && openAudioInputDeviceSettings(mediaSource)}
            >
                {hasDawAudioError && (
                    <ErrorIndicator
                        text={t('Error occurred: click to open settings.')}
                        onClick={() => sessionSettingsTrigger.trigger({ tab: 'audio', section: 'audio-daw' })}
                    />
                )}

                {hasComputerAudioError && (
                    <ErrorIndicator
                        text={t('Error occurred: click to open settings.')}
                        onClick={() => sessionSettingsTrigger.trigger({ tab: 'audio', section: 'audio-computer' })}
                    />
                )}
            </DeviceLabelRow>
            {atrack.info.channelSelection.map((chgrp) => (
                <ChannelGroupControls
                    key={JSON.stringify(chgrp)}
                    atrack={atrack}
                    chgrp={chgrp}
                    volumeControlError={volumeControlError}
                    toggleMyAudioDeviceTalkback={toggleMyAudioDeviceTalkback}
                    toggleAutoMute={toggleAutoMute}
                    toggleMyAudioDeviceMute={toggleMyAudioDeviceMute}
                    openRecordTalkbackKeyDialog={openRecordTalkbackKeyDialog}
                    set_volumeControlError={set_volumeControlError}
                />
            ))}
        </DeviceContainer>
    )
}

type ChannelGroupControlsProps = {
    atrack: AudioTrackWithInfo
    chgrp: number[]
    volumeControlError: string | null
    toggleMyAudioDeviceTalkback: any
    toggleAutoMute: (deviceId: string) => void
    toggleMyAudioDeviceMute: any
    openRecordTalkbackKeyDialog: any
    set_volumeControlError: (msg: string) => void
}

function ChannelGroupControls({
    atrack,
    chgrp,
    volumeControlError,
    toggleMyAudioDeviceTalkback,
    toggleAutoMute,
    toggleMyAudioDeviceMute,
    openRecordTalkbackKeyDialog,
    set_volumeControlError,
}: ChannelGroupControlsProps) {
    const { t } = useTranslation()

    const { state: roomState } = useContext(RoomContext) as RoomContextType
    const { userStateRef } = useUserContextRef()

    const { updateSelectedAudioDeviceRemoveChannelGroup, toggleAudioDeviceSelection } = useMediaSelectionActions()

    const isAudioEngine = !!atrack.info.audioEngineStreamUuid

    // the gainValue used strictly for displaying on the slider.
    const [gainValue, set_gainValue] = useState<number>(1)

    // when the volume dial is moved, this value is changed. We separate this out from `gainValue`
    // to avoid feedback loop.
    const [gainValueFromUserControl, set_gainValueFromUserControl] = useState<number>(1)
    const gainValueFromUserControlDebounced = useDebounce<number>(gainValueFromUserControl, 500, {
        maxSuppressedMs: 500,
    })
    const gainValueIsfirst = useRef<boolean>(true)

    const isTalkbackEnabled = roomState.myAudioTalkbackState[encodeExtendedDeviceId(atrack.info.deviceId, chgrp)]
    const isAutoMuteEnabled = roomState.autoMuteEnabled[encodeExtendedDeviceId(atrack.info.deviceId, chgrp)]
    const isMuted = roomState.myAudioMuteState[encodeExtendedDeviceId(atrack.info.deviceId, chgrp)]

    const isAutoMuted = useMemo(() => {
        if (!isAutoMuteEnabled) {
            return false
        }
        for (const [deviceId, triggering] of Object.entries(roomState.autoMuteTriggeringStreams)) {
            if (triggering && deviceId !== atrack.info.deviceId) {
                return true
            }
        }
        return false
    }, [isAutoMuteEnabled, atrack.info, roomState.autoMuteTriggeringStreams])

    useEffect(() => {
        if (gainValueIsfirst.current) {
            gainValueIsfirst.current = false
            return
        }

        const value = gainValueFromUserControlDebounced

        if (isAudioEngine) {
            const media = atrack as unknown as FakeMediaStreamTrack
            media.setVolumeOfChannelGroup(value, chgrp)
        } else {
            const nativeDeviceId = atrack.info.nativeDeviceId
            console.log(`nativeDeviceId: ${nativeDeviceId} gainValue_t: ` + value)

            if (nativeDeviceId != null) {
                // If is system audio (BlackHole, Muse Audio Share, etc., then
                // we must set its output, not its input.)
                const isInput = atrack.info.isSystemAudio ? false : true

                ElectronIPC.setSystemDeviceVolume({ nativeDeviceId, value, isInput })
            }
        }
    }, [gainValueFromUserControlDebounced])

    useEffect(() => {
        const handler = (externalEvent: ExternalEvent) => {
            if (externalEvent.eventName !== 'get-vol') {
                return
            }

            console.log(`AudioTrackControl (label=${atrack.label}): Got external event: %O`, externalEvent)

            // externalEvent.value
            const nativeDeviceId = externalEvent.value?.nativeDeviceId
            if (nativeDeviceId != atrack.info.nativeDeviceId) {
                console.log(
                    `AudioTrackControl (label=${atrack.label}): Skipping this externalEvent because ` +
                    `the nativeDeviceId=${nativeDeviceId} does not match.`
                )
                return
            }

            const errorCode = externalEvent.value?.errorCode
            const volume = externalEvent.value?.volume
            if (errorCode == 0) {
                // check if device is muted.
                if (userStateRef.current.mediaSourceStatus.audioInputs[atrack.info.deviceId]?.isMuted) {
                    set_volumeControlError('device_muted')
                } else if (isAudioEngine) {
                    // no-op
                } else if (typeof volume === 'number') {
                    set_gainValue(volume)
                }
            } else {
                set_volumeControlError(errorCode + '')
            }
        }

        ExternalEventEmitter.on('event', handler)

        return () => {
            ExternalEventEmitter.removeListener('event', handler)
        }
    }, [])

    const value = (() => {
        if (isAudioEngine) {
            return gainValue
        } else {
            if (atrack.info.isMuseAudioShare || atrack.info.isMuseDAWBridge) {
                return 1.0
            }
            return volumeControlError ? 0.5 : gainValue
        }
    })()

    const [isRemoving, setIsRemoving] = useState(false)

    const isOnlyChannelGroup = atrack.info.channelSelection.length <= 1

    const onRemoveChannel = () => {
        if (isOnlyChannelGroup) {
            return
        }

        const mediaSelection = userStateRef.current.mediaSelection.audioInputs.find(
            (a) => a.deviceId === atrack.info.deviceId
        )

        if (mediaSelection) {
            updateSelectedAudioDeviceRemoveChannelGroup(mediaSelection, chgrp)
            setIsRemoving(true)
        }
    }

    const handleRemoveDevice = () => {
        const source = userStateRef.current.mediaSources.audioInputs.find(
            (inp) => inp.deviceId === atrack.info.deviceId
        )
        if (source) {
            setIsRemoving(true)
            toggleAudioDeviceSelection(source)
        }
    }

    const extDeviceId = encodeExtendedDeviceId(atrack.info.deviceId, chgrp)
    const chLabel = chgrp.map((n) => n + 1).join('-')

    const disabledSliderTooltip = () => {
        if (!volumeControlError) {
            return null
        }

        let text = ''
        if (volumeControlError === 'os_not_supported') {
            text = t(`In-app volume control is not yet supported on this OS.`)
        } else if (volumeControlError === 'device_muted') {
            text = t(`The device is muted. You can unmute this device in Audio MIDI Setup.`)
        } else if (volumeControlError === 'muse_system_audio') {
            text = 'The volume for this device must be set to 1.'
        } else {
            text = t(`This device does not support master volume control. Error code: `) + volumeControlError
        }

        return <div className="max-w-[300px]">{text}</div>
    }

    return (
        <ChannelGroupContainer>
            <ChannelGroupMeter atrack={atrack} chgrp={chgrp} chLabel={chLabel} isMuted={isMuted} />

            <Tooltip text={disabledSliderTooltip()}>
                <div className="grow">
                    <Slider
                        disabled={isMuted || !!volumeControlError}
                        min={0}
                        max={1}
                        step={0.01}
                        value={value}
                        onChange={(newValue) => {
                            if (!volumeControlError) {
                                set_gainValueFromUserControl(newValue)
                                set_gainValue(newValue)
                            }
                        }}
                    />
                </div>
            </Tooltip>

            <ChannelGroupControlButtons
                isMuted={isMuted}
                isTalkbackEnabled={isTalkbackEnabled}
                showAutoMute={true}
                isAutoMuted={isAutoMuted}
                isAutoMuteEnabled={isAutoMuteEnabled}
                onTalkbackToggled={() => toggleMyAudioDeviceTalkback(extDeviceId)}
                onAutoMuteToggled={() => toggleAutoMute(extDeviceId)}
                onMuteToggled={() => toggleMyAudioDeviceMute(extDeviceId)}
                set_isRecordKeyDialogOpen={openRecordTalkbackKeyDialog}
                isOnlyChannelGroup={isOnlyChannelGroup}
                isRemoving={isRemoving}
                onRemoveDevice={handleRemoveDevice}
                onRemoveChannel={onRemoveChannel}
            />
        </ChannelGroupContainer>
    )
}

type ChannelGroupControlButtonsProps = {
    isMuted: boolean | undefined
    isTalkbackEnabled: boolean | undefined
    showAutoMute: boolean | undefined
    isAutoMuted: boolean | undefined
    isAutoMuteEnabled: boolean | undefined
    onTalkbackToggled: () => void
    onMuteToggled: () => void
    onAutoMuteToggled: () => void
    set_isRecordKeyDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
    onRemoveChannel: () => void
    onRemoveDevice: () => void
    isOnlyChannelGroup: boolean
    isRemoving: boolean
}

function ChannelGroupControlButtons({
    isMuted,
    isTalkbackEnabled,
    showAutoMute,
    isAutoMuted,
    isAutoMuteEnabled,
    onTalkbackToggled,
    onAutoMuteToggled,
    onMuteToggled,
    set_isRecordKeyDialogOpen,
    onRemoveChannel,
    onRemoveDevice,
    isOnlyChannelGroup,
    isRemoving,
}: ChannelGroupControlButtonsProps) {
    const { t } = useTranslation()

    const { logUserActivityWithContext } = useAnalytics()

    const talkbackButtonTooltipText = isMuted ? t('Disable push-to-talk') : t('Enable push-to-talk')

    const viewMode = useViewMode()

    return (
        <div className="flex gap-[2px]">
            <MenuButton
                triggerClassName="h-[30px] max-h-[30px] rounded-[10px] shadow-glow md:grid-cols-[1fr_40%] md:min-w-[50px] bg-white/3"
                innerButtonClassName="bg-transparent px-0 md:px-0"
                separator
                icon={
                    <div className="flex items-center relative">
                        <MuteSubStateIndicator
                            autoMuteEnabled={isAutoMuteEnabled}
                            talkbackEnabled={isTalkbackEnabled}
                        />
                        {/* Unsure if scale is the right approach here.... */}
                        {isMuted || isAutoMuted ? (
                            <MicrophoneOffIcon className="text-muse-danger" style={{ fontSize: '1.3rem' }} />
                        ) : (
                            <MicrophoneIcon style={{ fontSize: '1.3rem' }} />
                        )}
                    </div>
                }
                placement="bottom"
                onIconClick={() => {
                    onMuteToggled()
                    logUserActivityWithContext(AnalyticsEvents.CLICKED_MUTE_AUDIO_TOGGLE_BUTTON, {
                        componentSource: 'MyVideoPreviewControls',
                        toggleState: !isMuted,
                    })
                }}
                contentClassName="gap-[5px] p-0"
                content={
                    <>
                        {showAutoMute && (
                            <MenuItem className="self-stretch">
                                <Tooltip
                                    variant="info"
                                    position="left"
                                    text={t('Auto mute this device if another device is streaming HQ audio')}
                                    noNativeWrapper
                                >
                                    <Button
                                        variant="rectangle"
                                        className="w-full justify-between pr-2"
                                        onClick={() => {
                                            onAutoMuteToggled()
                                            logUserActivityWithContext(
                                                AnalyticsEvents.CLICKED_AUTO_MUTE_AUDIO_TOGGLE_BUTTON,
                                                {
                                                    componentSource: 'MyVideoPreviewControls',
                                                    toggleState: !isAutoMuteEnabled,
                                                }
                                            )
                                        }}
                                    >
                                        <div className="flex items-center gap-1">
                                            <VolumeOffIcon /> {t('Auto Mute')}
                                        </div>
                                        <Checkbox checked={isAutoMuteEnabled} />
                                    </Button>
                                </Tooltip>
                            </MenuItem>
                        )}
                        {!viewMode.fullscreen ? (
                            <MenuItem className="self-stretch">
                                <Tooltip
                                    variant="info"
                                    position="left"
                                    text={
                                        <TalkbackTooltip
                                            message={talkbackButtonTooltipText}
                                            set_isRecordKeyDialogOpen={set_isRecordKeyDialogOpen}
                                        />
                                    }
                                    noNativeWrapper
                                >
                                    <Button
                                        variant="rectangle"
                                        className="w-full justify-between pr-2"
                                        onClick={() => {
                                            // Starts IOHook if not started.
                                            if (!isTalkbackEnabled) {
                                                ElectronIPC.iohookSetMode('normal')
                                            }

                                            onTalkbackToggled()
                                            logUserActivityWithContext(AnalyticsEvents.CLICKED_TALKBACK_TOGGLE_BUTTON, {
                                                componentSource: 'MyVideoPreviewControls',
                                                toggleState: !isTalkbackEnabled,
                                                fullscreen: viewMode.fullscreen,
                                            })
                                        }}
                                    >
                                        <div className="flex items-center gap-1">
                                            <TalkbackIcon /> {t('Push-to-Talk')}
                                        </div>
                                        <Checkbox checked={isTalkbackEnabled} />
                                    </Button>
                                </Tooltip>
                            </MenuItem>
                        ) : (
                            <></>
                        )}
                    </>
                }
            />
            <Button
                disabled={isRemoving}
                onClick={() => {
                    if (isOnlyChannelGroup) {
                        onRemoveDevice()
                    } else {
                        onRemoveChannel()
                        logUserActivityWithContext(AnalyticsEvents.CLICKED_REMOVE_CHANNEL_BUTTON, {
                            componentSource: 'MyVideoPreviewControls',
                        })
                    }
                }}
                className={cn('-mr-2 min-w-[22px]', isRemoving && 'opacity-50 px-[3px]')}
            >
                {isRemoving ? <Loader width="16px" bars={3} /> : <ClearIcon />}
            </Button>
        </div>
    )
}

type MuteSubStateIndicatorProps = {
    autoMuteEnabled?: boolean
    talkbackEnabled?: boolean
}
function MuteSubStateIndicator(props: MuteSubStateIndicatorProps) {
    const symbol =
        props.autoMuteEnabled && props.talkbackEnabled
            ? '*'
            : props.autoMuteEnabled
              ? 'A'
              : props.talkbackEnabled
                ? 'T'
                : null

    if (symbol === null) {
        return null
    }

    return <div className="absolute top-[-5px] left-[-3px] text-muse-yellow font-light text-[10px]">{symbol}</div>
}

function TalkbackTooltip({
    message,
    set_isRecordKeyDialogOpen,
}: {
    message: string
    set_isRecordKeyDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
}) {
    const { t } = useTranslation()

    return (
        <>
            <div className="flex flex-col gap-[2px] font-normal">
                <div>{message}</div>
                <div>
                    {t(
                        'When push-to-talk is enabled, the selected device will be muted until you hold the trigger key.'
                    )}
                </div>
                {env.OS === 'darwin' && (
                    <div>{t('Muse needs Accessibility permission for the trigger key to work.')}</div>
                )}
                <div>
                    {t('Current trigger key:')}{' '}
                    {makeTalkbackKeyDescriptionString(persistent.getObject(keyboardshortcut_talkback))}
                </div>
                <a
                    href={'#'}
                    className="text-muse-yellow"
                    onClick={(e) => {
                        e.preventDefault()
                        set_isRecordKeyDialogOpen(true)
                    }}
                    style={{ display: 'inline-flex' }}
                >
                    <MicrophoneIcon className="mr-[3px]" /> {t('Change trigger key')}
                </a>
            </div>
        </>
    )
}
